import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react'
import { useCssHandles } from '../../../shared/hooks/useCssHandles'
import { AdminLayout, AdminFilterTrigger, AdminFilterSidebar } from '../components'
import { Modal, ConfirmModal, Select, Input } from '../../../shared/components'
import adminReviewService from '../../../services/adminReviewService'
import {
  AdminReview,
  AdminReviewSummary,
  PaginationResponse
} from '../../../types/api'
import { colors } from '../../../styles/design-system'

type ReviewStatus = 'pending' | 'approved' | 'rejected'

type ReviewSortField =
  | 'createdAt'
  | 'customerName'
  | 'productName'
  | 'rating'
  | 'status'
  | 'helpfulVotes'

type StatusFilter = 'all' | ReviewStatus

const CSS_HANDLES = [
  'adminReviews',
  'reviewsHeader',
  'reviewsTitle',
  'reviewsSubtitle',
  'reviewsStats',
  'reviewsStatCard',
  'reviewsActions',
  'reviewsList',
  'reviewCard',
  'reviewHeader',
  'reviewMeta',
  'reviewContent',
  'reviewActions',
  'statusBadge',
  'verifiedBadge',
  'emptyState',
  'errorState',
  'actionError'
] as const

const ITEMS_PER_PAGE = 10

const DEFAULT_FILTERS: { search: string; status: StatusFilter } = {
  search: '',
  status: 'all'
}

const DEFAULT_SUMMARY: AdminReviewSummary = {
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  averageRating: 0
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return '—'
  }

  return parsed.toLocaleDateString('pt-BR')
}

const renderStars = (rating: number) =>
  Array.from({ length: 5 }).map((_, index) => (
    <span
      key={`review-rating-star-${index}`}
      className={index < rating ? 'text-yellow-400' : 'text-gray-300'}
      aria-hidden="true"
    >
      ★
    </span>
  ))

const getStatusLabel = (status: ReviewStatus) => {
  switch (status) {
    case 'pending':
      return 'Pendente'
    case 'approved':
      return 'Aprovado'
    case 'rejected':
      return 'Rejeitado'
    default:
      return status
  }
}

const getStatusClasses = (status: ReviewStatus) => {
  switch (status) {
    case 'pending':
      return 'bg-amber-50 text-amber-600 ring-1 ring-inset ring-amber-200'
    case 'approved':
      return 'bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-200'
    case 'rejected':
      return 'bg-red-50 text-red-600 ring-1 ring-inset ring-red-200'
    default:
      return 'bg-gray-100 text-gray-600'
  }
}

const SORT_OPTIONS: Array<{ field: ReviewSortField; label: string }> = [
  { field: 'createdAt', label: 'Data' },
  { field: 'rating', label: 'Avaliação' },
  { field: 'customerName', label: 'Cliente' },
  { field: 'productName', label: 'Produto' },
  { field: 'status', label: 'Status' },
  { field: 'helpfulVotes', label: 'Útil' }
]

const AdminReviews: FC = () => {
  const handles = useCssHandles(CSS_HANDLES)
  const [reviews, setReviews] = useState<AdminReview[]>([])
  const [summary, setSummary] =
    useState<AdminReviewSummary>(DEFAULT_SUMMARY)
  const [pagination, setPagination] =
    useState<PaginationResponse | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [filters, setFilters] = useState<{ search: string; status: StatusFilter }>(
    () => ({
      ...DEFAULT_FILTERS
    })
  )
  const [draftFilters, setDraftFilters] = useState<{
    search: string
    status: StatusFilter
  }>(() => ({
    ...DEFAULT_FILTERS
  }))
  const [sortState, setSortState] = useState<{
    field: ReviewSortField
    order: 'asc' | 'desc'
  }>({
    field: 'createdAt',
    order: 'desc'
  })
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false)
  const [isFetching, setIsFetching] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [selectedReview, setSelectedReview] =
    useState<AdminReview | null>(null)
  const [mutatingReviewId, setMutatingReviewId] = useState<string | null>(null)
  const [removingReviewId, setRemovingReviewId] = useState<string | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [reviewToDelete, setReviewToDelete] = useState<AdminReview | null>(null)

  // Refs para evitar chamadas duplicadas
  const previousFiltersRef = useRef<string | null>(null)
  const previousPageRef = useRef<number>(1)
  const previousSortStateRef = useRef<string | null>(null)
  const isLoadingRef = useRef(false)

  const fetchReviews = useCallback(
    async (page: number) => {
      // Verificar se já está carregando para evitar chamadas simultâneas
      if (isLoadingRef.current) {
        return
      }

      // Serializar filtros e sortState para comparação
      const filtersKey = JSON.stringify({
        search: filters.search.trim(),
        status: filters.status
      })
      const sortStateKey = JSON.stringify({
        field: sortState.field,
        order: sortState.order
      })

      // Verificar se realmente mudou
      const filtersChanged = previousFiltersRef.current !== filtersKey
      const pageChanged = previousPageRef.current !== page
      const sortStateChanged = previousSortStateRef.current !== sortStateKey

      // Se nada mudou e já foi carregado, não fazer nova chamada
      if (!filtersChanged && !pageChanged && !sortStateChanged && isLoadingRef.current) {
        return
      }
      if (!filtersChanged && !pageChanged && !sortStateChanged && !isLoadingRef.current && previousFiltersRef.current === filtersKey) {
        return
      }

      isLoadingRef.current = true
      previousFiltersRef.current = filtersKey
      previousPageRef.current = page
      previousSortStateRef.current = sortStateKey

      setIsFetching(true)
      setError(null)

      try {
        const trimmedSearch = filters.search.trim()
        const statusParam =
          filters.status === 'all' ? undefined : filters.status

        const data = await adminReviewService.list({
          page,
          limit: ITEMS_PER_PAGE,
          search: trimmedSearch || undefined,
          status: statusParam,
          sortBy: sortState.field,
          sortOrder: sortState.order
        })

        const nextSummary = data.summary
          ? { ...DEFAULT_SUMMARY, ...data.summary }
          : { ...DEFAULT_SUMMARY }

        setReviews(data.reviews)
        setSummary(nextSummary)
        setPagination(data.pagination)
        setHasLoaded(true)
      } catch (err) {
        console.error('Erro ao carregar reviews:', err)
        setReviews([])
        setSummary(DEFAULT_SUMMARY)
        setPagination(null)
        setError('Não foi possível carregar os reviews. Tente novamente.')
        setHasLoaded(true)
        // Resetar refs em caso de erro para permitir nova tentativa
        previousFiltersRef.current = null
        previousPageRef.current = 1
        previousSortStateRef.current = null
      } finally {
        setIsFetching(false)
        isLoadingRef.current = false
      }
    },
    [filters, sortState.field, sortState.order]
  )

  useEffect(() => {
    fetchReviews(currentPage)
  }, [fetchReviews, currentPage])

  const handleOpenFilters = () => {
    setDraftFilters({ ...filters })
    setIsFilterSidebarOpen(true)
  }

  const handleCloseFilters = () => {
    setIsFilterSidebarOpen(false)
  }

  const handleDraftFilterChange = <
    Key extends keyof typeof draftFilters
  >(
    key: Key,
    value: (typeof draftFilters)[Key]
  ) => {
    setDraftFilters((previous) => ({
      ...previous,
      [key]: value
    }))
  }

  const handleApplyFilters = () => {
    setFilters({ ...draftFilters })
    setCurrentPage(1)
    setIsFilterSidebarOpen(false)
  }

  const handleClearFilters = () => {
    const reset = { ...DEFAULT_FILTERS }
    setDraftFilters(reset)
    setFilters(reset)
    setCurrentPage(1)
  }

  const isApplyDisabled =
    draftFilters.search === filters.search &&
    draftFilters.status === filters.status

  const handleSort = (field: ReviewSortField) => {
    setSortState((previous) => {
      const nextOrder =
        previous.field === field && previous.order === 'asc'
          ? 'desc'
          : 'asc'

      return {
        field,
        order: previous.field === field ? nextOrder : 'asc'
      }
    })
    setCurrentPage(1)
  }

  const renderSortIndicator = (field: ReviewSortField) => {
    if (sortState.field !== field) {
      return (
        <span
          style={{ marginLeft: 6, color: colors.neutral.gray300 }}
          aria-hidden="true"
        >
          ⇅
        </span>
      )
    }

    return (
      <span
        style={{
          marginLeft: 6,
          color: colors.primary.blue,
          display: 'inline-flex',
          alignItems: 'center'
        }}
        aria-hidden="true"
      >
        {sortState.order === 'asc' ? '↑' : '↓'}
      </span>
    )
  }

  const handleApproveReview = async (reviewId: string) => {
    setMutatingReviewId(reviewId)
    setActionError(null)

    try {
      await adminReviewService.approve(reviewId)
      await fetchReviews(currentPage)
      setSelectedReview((previous) =>
        previous && previous.id === reviewId ? null : previous
      )
    } catch (err) {
      console.error('Erro ao aprovar review:', err)
      setActionError('Não foi possível aprovar o review. Tente novamente.')
    } finally {
      setMutatingReviewId(null)
    }
  }

  const handleRejectReview = async (reviewId: string) => {
    setMutatingReviewId(reviewId)
    setActionError(null)

    try {
      await adminReviewService.reject(reviewId)
      await fetchReviews(currentPage)
      setSelectedReview((previous) =>
        previous && previous.id === reviewId ? null : previous
      )
    } catch (err) {
      console.error('Erro ao rejeitar review:', err)
      setActionError('Não foi possível rejeitar o review. Tente novamente.')
    } finally {
      setMutatingReviewId(null)
    }
  }

  const handleDeleteReview = (review: AdminReview) => {
    setReviewToDelete(review)
    setDeleteConfirmOpen(true)
  }

  const confirmDeleteReview = async () => {
    if (!reviewToDelete) return

    setRemovingReviewId(reviewToDelete.id)
    setActionError(null)
    setDeleteConfirmOpen(false)

    try {
      await adminReviewService.remove(reviewToDelete.id)
      const shouldGoBackPage =
        reviews.length === 1 && (pagination?.page ?? currentPage) > 1

      if (shouldGoBackPage) {
        const previousPage = Math.max(
          1,
          (pagination?.page ?? currentPage) - 1
        )
        setCurrentPage(previousPage)
        await fetchReviews(previousPage)
      } else {
        await fetchReviews(currentPage)
      }

      setSelectedReview((previous) =>
        previous && previous.id === reviewToDelete.id ? null : previous
      )
    } catch (err) {
      console.error('Erro ao excluir review:', err)
      setActionError('Não foi possível excluir o review. Tente novamente.')
    } finally {
      setRemovingReviewId(null)
      setReviewToDelete(null)
    }
  }

  const hasActiveFilters = useMemo(
    () => filters.status !== 'all' || filters.search.trim() !== '',
    [filters.search, filters.status]
  )

  const activeFiltersDescriptions = useMemo(
    () =>
      [
        filters.status !== 'all'
          ? `status: ${
              filters.status === 'pending'
                ? 'pendentes'
                : filters.status === 'approved'
                ? 'aprovados'
                : 'rejeitados'
            }`
          : null,
        filters.search.trim()
          ? `busca: “${filters.search.trim()}”`
          : null
      ]
        .filter(Boolean)
        .join(' • '),
    [filters.search, filters.status]
  )

  const totalPages = pagination?.totalPages ?? 1
  const totalItems =
    pagination?.total ?? summary.total ?? reviews.length
  const currentPageNumber = pagination?.page ?? currentPage
  const limit = pagination?.limit ?? ITEMS_PER_PAGE
  const startIndex =
    totalItems === 0 ? 0 : (currentPageNumber - 1) * limit + 1
  const endIndex =
    totalItems === 0
      ? 0
      : Math.min(startIndex + reviews.length - 1, totalItems)
  const showInitialSkeleton = !hasLoaded && isFetching
  const showLoadingOverlay = hasLoaded && isFetching
  const showEmptyState =
    hasLoaded && !isFetching && reviews.length === 0 && !error

  const renderSkeleton = () => (
    <AdminLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="mx-auto max-w-7xl space-y-8 px-4 py-8">
          <div className="space-y-3">
            <div className="h-8 w-64 animate-pulse rounded bg-gray-200" />
            <div className="h-4 w-80 animate-pulse rounded bg-gray-200" />
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`reviews-skeleton-card-${index}`}
                className="h-32 animate-pulse rounded-xl border border-gray-100 bg-white shadow-sm"
              />
            ))}
          </div>

          <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="h-10 w-full animate-pulse rounded bg-gray-100 md:w-80" />
              <div className="h-10 w-full animate-pulse rounded bg-gray-100 md:w-52" />
            </div>
          </div>

          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={`reviews-skeleton-row-${index}`}
                className="h-28 animate-pulse rounded-xl border border-gray-100 bg-white shadow-sm"
              />
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  )

  if (showInitialSkeleton) {
    return renderSkeleton()
  }

  return (
    <>
    <AdminLayout>
      <div className="relative">
        {showLoadingOverlay && (
          <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/70">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
          </div>
        )}

        <div className={`${handles.adminReviews} min-h-screen bg-gray-50`}>
          <div className="mx-auto max-w-7xl space-y-8 px-4 py-8">
            {/* Header */}
            <div className={handles.reviewsHeader}>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h1 className={`${handles.reviewsTitle} text-3xl font-bold text-gray-900`}>
                    Moderação de Reviews
                  </h1>
                  <p className={`${handles.reviewsSubtitle} mt-2 text-lg text-gray-600`}>
                    Gerencie avaliações e comentários dos clientes
                  </p>
                </div>
              </div>
            </div>

            {/* Stats */}
            <div className={`${handles.reviewsStats} grid gap-4 md:grid-cols-3`}>
              <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-gray-500">Total de reviews</p>
                <p className="mt-2 text-3xl font-bold text-gray-900">{summary.total}</p>
                <p className="mt-1 text-sm text-gray-500">
                  {summary.approved} aprovados • {summary.pending} pendentes
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-gray-500">Pendentes</p>
                <p className="mt-2 text-3xl font-bold text-amber-500">{summary.pending}</p>
                <p className="mt-1 text-sm text-gray-500">
                  Priorize avaliações que aguardam moderação
                </p>
              </div>
              <div className="rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
                <p className="text-sm font-medium text-gray-500">Avaliação média</p>
                <p className="mt-2 text-3xl font-bold text-blue-600">
                  {summary.total === 0 ? '—' : summary.averageRating.toFixed(1)}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  Baseado em {summary.total} avaliações
                </p>
              </div>
            </div>

            {error && !isFetching && (
              <div
                className={`${handles.errorState} rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700`}
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <span>{error}</span>
                  <button
                    onClick={() => fetchReviews(currentPageNumber)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                  >
                    Tentar novamente
                  </button>
                </div>
              </div>
            )}

            {actionError && (
              <div
                className={`${handles.actionError} rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700`}
              >
                {actionError}
              </div>
            )}

            {/* Action bar */}
            <div className={`${handles.reviewsActions} rounded-xl border border-gray-100 bg-white p-6 shadow-sm`}>
              <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm text-gray-600">
                    Exibindo{' '}
                    <span className="font-semibold text-gray-900">
                      {totalItems === 0 ? 0 : startIndex} - {endIndex}
                    </span>{' '}
                    de{' '}
                    <span className="font-semibold text-gray-900">{totalItems}</span>{' '}
                    reviews
                  </p>
                  {hasActiveFilters && (
                    <p className="mt-1 text-xs text-blue-600">
                      Filtros aplicados: {activeFiltersDescriptions}
                    </p>
                  )}
                </div>
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-end">
                  <div className="flex flex-wrap items-center gap-2">
                    {SORT_OPTIONS.map((option) => (
                      <button
                        key={option.field}
                        type="button"
                        onClick={() => handleSort(option.field)}
                        className={`inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                          sortState.field === option.field
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {option.label}
                        {renderSortIndicator(option.field)}
                      </button>
                    ))}
                  </div>
                  <AdminFilterTrigger
                    onClick={handleOpenFilters}
                    hasActiveFilters={hasActiveFilters}
                  />
                </div>
              </div>
            </div>

            {/* Reviews List */}
            <div className={`${handles.reviewsList} space-y-4`}>
              {showEmptyState ? (
                <div className={`${handles.emptyState} rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center`}>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Nenhum review encontrado
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Ajuste os filtros para encontrar outras avaliações.
                  </p>
                </div>
              ) : (
                reviews.map((review) => {
                  const isMutating = mutatingReviewId === review.id
                  const isRemoving = removingReviewId === review.id

                  return (
                    <article
                      key={review.id}
                      className={`${handles.reviewCard} rounded-xl border border-gray-100 bg-white p-6 shadow-sm transition-colors hover:border-blue-200`}
                    >
                      <div className={`${handles.reviewHeader} flex flex-col gap-4 md:flex-row md:justify-between`}>
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {review.title}
                            </h3>
                            <div className="flex items-center gap-1 text-lg" aria-label={`Avaliação ${review.rating} de 5`}>
                              {renderStars(review.rating)}
                            </div>
                            {review.verifiedPurchase && (
                              <span className={`${handles.verifiedBadge} inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-600 ring-1 ring-inset ring-emerald-200`}>
                                ✓ Verificado
                              </span>
                            )}
                          </div>
                          <div className={`${handles.reviewMeta} flex flex-wrap items-center gap-4 text-xs text-gray-500`}>
                            <span>
                              Cliente:{' '}
                              <strong className="text-gray-900">{review.customerName}</strong>
                            </span>
                            <span>
                              Produto:{' '}
                              <strong className="text-gray-900">{review.productName}</strong>
                            </span>
                            <span>
                              Data:{' '}
                              <strong className="text-gray-900">{formatDate(review.createdAt)}</strong>
                            </span>
                            <span>
                              Útil:{' '}
                              <strong className="text-gray-900">{review.helpfulVotes}</strong>
                            </span>
                          </div>
                          <p className={`${handles.reviewContent} text-sm leading-relaxed text-gray-700`}>
                            {review.comment}
                          </p>
                        </div>
                        <div className="flex flex-col items-start gap-3 md:items-end">
                          <span
                            className={`${handles.statusBadge} inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${getStatusClasses(review.status)}`}
                          >
                            {getStatusLabel(review.status)}
                          </span>
                          <div className={`${handles.reviewActions} flex flex-wrap gap-2`}>
                            {review.status === 'pending' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => handleApproveReview(review.id)}
                                  disabled={isMutating || isRemoving}
                                  className="inline-flex items-center rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  Aprovar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleRejectReview(review.id)}
                                  disabled={isMutating || isRemoving}
                                  className="inline-flex items-center rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  Rejeitar
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => setSelectedReview(review)}
                              className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                            >
                              Ver detalhes
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteReview(review)}
                              disabled={isRemoving || isMutating}
                              className="inline-flex items-center rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {isRemoving ? 'Excluindo...' : 'Excluir'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  )
                })
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex flex-col items-center justify-between gap-4 border-t border-gray-100 px-6 py-4 text-sm text-gray-500 md:flex-row">
                <p>
                  Página {currentPageNumber} de {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPageNumber === 1}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((page) => Math.min(totalPages, page + 1))
                    }
                    disabled={currentPageNumber === totalPages}
                    className="inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Próxima
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <AdminFilterSidebar
          isOpen={isFilterSidebarOpen}
          onClose={handleCloseFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          isApplyDisabled={isApplyDisabled}
          applyLabel="Aplicar filtros"
          clearLabel="Limpar filtros"
        >
          <div className="space-y-6">
            <div>
              <label
                htmlFor="reviews-filter-search"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Buscar reviews
              </label>
              <Input
                id="reviews-filter-search"
                type="text"
                value={draftFilters.search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDraftFilterChange('search', event.target.value)
                }
                className="w-full rounded-lg border border-gray-300 px-4 py-3 text-sm text-gray-700 shadow-sm transition-shadow focus:border-blue-500"
                placeholder="Cliente, produto ou título"
              />
            </div>

            <div>
              <label
                htmlFor="reviews-filter-status"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Status
              </label>
              <Select
                id="reviews-filter-status"
                value={draftFilters.status}
                onChange={(value) =>
                  handleDraftFilterChange('status', value as StatusFilter)
                }
                options={[
                  { value: 'all', label: 'Todos' },
                  { value: 'pending', label: 'Pendentes' },
                  { value: 'approved', label: 'Aprovados' },
                  { value: 'rejected', label: 'Rejeitados' }
                ]}
                placeholder="Selecione o status"
                className="w-full"
              />
            </div>
          </div>
        </AdminFilterSidebar>

        <Modal
          isOpen={Boolean(selectedReview)}
          onClose={() => setSelectedReview(null)}
          title="Detalhes do review"
          size="md"
          titleClassName="text-blue-600"
          footer={
            selectedReview ? (
              <div className="flex items-center justify-end gap-3">
                {selectedReview.status === 'pending' && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleApproveReview(selectedReview.id)}
                      disabled={mutatingReviewId === selectedReview.id || removingReviewId === selectedReview.id}
                      className="inline-flex items-center rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Aprovar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRejectReview(selectedReview.id)}
                      disabled={mutatingReviewId === selectedReview.id || removingReviewId === selectedReview.id}
                      className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Rejeitar
                    </button>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => setSelectedReview(null)}
                  className="inline-flex items-center rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                >
                  Fechar
                </button>
              </div>
            ) : undefined
          }
        >
          {selectedReview && (
            <div className="space-y-6">
              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Informações gerais
                </h3>
                <div className="mt-3 space-y-2 text-sm text-gray-700">
                  <p>
                    <span className="font-medium text-gray-900">Título:</span>{' '}
                    {selectedReview.title}
                  </p>
                  <p className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">Avaliação:</span>
                    <span className="flex items-center gap-1 text-lg">
                      {renderStars(selectedReview.rating)}
                    </span>
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Status:</span>{' '}
                    {getStatusLabel(selectedReview.status)}
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Data:</span>{' '}
                    {formatDate(selectedReview.createdAt)}
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Cliente:</span>{' '}
                    {selectedReview.customerName} ({selectedReview.customerEmail})
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Produto:</span>{' '}
                    {selectedReview.productName}
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Avaliações úteis:</span>{' '}
                    {selectedReview.helpfulVotes}
                  </p>
                  <p>
                    <span className="font-medium text-gray-900">Compra verificada:</span>{' '}
                    {selectedReview.verifiedPurchase ? 'Sim' : 'Não'}
                  </p>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                  Comentário
                </h3>
                <p className="mt-3 rounded-lg border border-gray-100 bg-gray-50 p-4 text-sm leading-relaxed text-gray-700">
                  {selectedReview.comment}
                </p>
              </section>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>

    <ConfirmModal
      isOpen={deleteConfirmOpen}
      onClose={() => {
        setDeleteConfirmOpen(false)
        setReviewToDelete(null)
      }}
      onConfirm={confirmDeleteReview}
      title="Confirmar Exclusão"
      message={`Tem certeza que deseja excluir esta avaliação? Esta ação não pode ser desfeita.`}
      confirmLabel="Excluir"
      cancelLabel="Cancelar"
      confirmButtonClassName="bg-red-600 hover:bg-red-700"
      isLoading={removingReviewId !== null}
    />
  </>
  )
}

export default AdminReviews


